import React, { useState, useCallback } from "react";
import Cropper from "react-easy-crop";
import {
  Box,
  Button,
  Flex,
  Text,
  Slider,
  SliderTrack,
  SliderFilledTrack,
  SliderThumb,
  useColorModeValue,
  Portal,
} from "@chakra-ui/react";
import { getCroppedImg } from "../util/cropImage";

const ImageCropper = ({ imageSrc, onCropDone, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const overlayBg = useColorModeValue("rgba(15, 23, 42, 0.65)", "rgba(0, 0, 0, 0.82)");
  const panelBg = useColorModeValue("rgba(255,255,255,0.98)", "rgba(18, 18, 20, 0.96)");
  const panelBorder = useColorModeValue("gray.200", "whiteAlpha.120");
  const headerBorder = useColorModeValue("gray.100", "whiteAlpha.080");
  const titleColor = useColorModeValue("gray.900", "white");
  const muted = useColorModeValue("gray.600", "whiteAlpha.600");
  const cropShade = useColorModeValue("rgba(15,23,42,0.55)", "rgba(0,0,0,0.65)");
  const cropBorderColor = useColorModeValue("rgba(52, 211, 153, 0.95)", "rgba(110, 231, 183, 0.9)");
  const panelShadow = useColorModeValue(
    "0 24px 80px rgba(15,23,42,0.18)",
    "0 24px 80px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.06)"
  );
  const sliderTrackBg = useColorModeValue("gray.200", "whiteAlpha.200");
  const cancelHoverBg = useColorModeValue("gray.100", "whiteAlpha.100");

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const finishCrop = async () => {
    if (isSaving || !croppedAreaPixels) return;
    setIsSaving(true);
    try {
      const { blob, fileUrl } = await getCroppedImg(imageSrc, croppedAreaPixels);
      await onCropDone(blob, fileUrl);
    } catch (error) {
      console.error("Crop failed:", error);
      setIsSaving(false);
    }
  };

  const cropSize = { width: 320, height: 320 };

  return (
    <Portal>
      <Flex
        position="fixed"
        inset={0}
        zIndex={2000}
        align="center"
        justify="center"
        px={4}
        py={6}
        bg={overlayBg}
        backdropFilter="blur(10px)"
        onClick={isSaving ? undefined : onCancel}
      >
        <Flex
          direction="column"
          w="full"
          maxW="480px"
          maxH={{ base: "90vh", md: "85vh" }}
          borderRadius="2xl"
          borderWidth="1px"
          borderColor={panelBorder}
          bg={panelBg}
          boxShadow={panelShadow}
          overflow="hidden"
          onClick={(e) => e.stopPropagation()}
        >
          <Flex
            align="center"
            justify="space-between"
            px={5}
            py={3.5}
            borderBottomWidth="1px"
            borderColor={headerBorder}
          >
            <Text fontSize="sm" fontWeight="semibold" color={titleColor} letterSpacing="-0.02em">
              Crop photo
            </Text>
            <Text fontSize="xs" color={muted}>
              Pinch or drag to frame your face
            </Text>
          </Flex>

          <Box position="relative" w="100%" h={{ base: "min(52vh, 380px)", md: "380px" }} bg="black">
            <Cropper
              image={imageSrc}
              cropSize={cropSize}
              crop={crop}
              zoom={zoom}
              aspect={1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              cropAreaStyle={{
                backgroundColor: cropShade,
                border: `2px solid ${cropBorderColor}`,
                boxShadow: "0 0 0 1px rgba(253,224,71,0.25)",
              }}
              style={{
                containerStyle: {
                  width: "100%",
                  height: "100%",
                  position: "relative",
                  backgroundColor: "#0a0a0b",
                },
                mediaStyle: {
                  maxWidth: "100%",
                  maxHeight: "100%",
                },
              }}
            />
          </Box>

          <Flex direction="column" gap={4} px={5} py={4}>
            <Flex align="center" gap={4}>
              <Text fontSize="xs" fontWeight="medium" color={muted} w="42px" flexShrink={0}>
                Zoom
              </Text>
              <Slider
                aria-label="Crop zoom"
                value={zoom}
                min={1}
                max={3}
                step={0.01}
                onChange={setZoom}
                flex={1}
                focusThumbOnChange={false}
              >
                <SliderTrack bg={sliderTrackBg} h="6px" borderRadius="full">
                  <SliderFilledTrack bgGradient="linear(to-r, brand.400, brand.500)" />
                </SliderTrack>
                <SliderThumb boxSize={4} borderWidth="2px" borderColor="white" boxShadow="md" />
              </Slider>
            </Flex>

            <Flex justify="flex-end" gap={2} flexWrap="wrap">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                color={muted}
                _hover={{ bg: cancelHoverBg }}
                onClick={onCancel}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                px={6}
                fontWeight="semibold"
                borderRadius="full"
                bgGradient="linear(to-r, brand.300, brand.500)"
                color="gray.900"
                borderWidth="1px"
                borderColor="whiteAlpha.300"
                boxShadow="0 2px 12px rgba(52,211,153,0.25), inset 0 1px 0 rgba(255,255,255,0.35)"
                _hover={{
                  bgGradient: "linear(to-r, brand.400, brand.600)",
                  transform: "translateY(-1px)",
                }}
                _active={{ transform: "translateY(0)" }}
                onClick={finishCrop}
                isLoading={isSaving}
                isDisabled={isSaving || !croppedAreaPixels}
                loadingText="Uploading"
              >
                Save photo
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
    </Portal>
  );
};

export default ImageCropper;
